import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { SlashMark } from './AmdenMark';
import { Reveal } from './Reveal';
import { scrollToId } from '@/lib/scroll';

export const CtaBand = ({
  index = '07',
  eyebrow = 'Next step',
  heading = 'Ready to improve how your business runs?',
  copy = 'Let’s discuss how Amden Group can help you build a more structured, scalable and successful business.',
  primaryLabel = 'Get Started',
  primaryHref = null,
  secondaryLabel = 'Explore Services',
  secondaryHref = '/services',
}) => {
  const navigate = useNavigate();
  return (
  <section
    aria-labelledby="cta-heading"
    data-testid="cta-band"
    className="relative overflow-hidden bg-navy-900 py-20 sm:py-28"
  >
    <div className="grain-overlay" aria-hidden="true" />
    <SlashMark
      className="pointer-events-none absolute -left-16 -top-16 h-72 w-72 opacity-[0.07]"
      bar="#ffffff"
      dot="#FFB800"
    />
    <SlashMark
      className="pointer-events-none absolute -bottom-20 -right-10 h-80 w-80 opacity-[0.05]"
      bar="#ffffff"
      dot="#FFB800"
    />

    <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
      <Reveal>
        <p className="flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-gold-500">
          <span className="font-mono">{index}</span> {eyebrow}
        </p>
        <h2 id="cta-heading" data-testid="cta-heading" className="mt-5 text-balance text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          {heading}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
          {copy}
        </p>
        <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
          {typeof primaryHref === 'function' ? (
            <button
              type="button"
              onClick={() => navigate(primaryHref())}
              data-testid="cta-get-started-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gold-600 px-7 py-4 text-base font-bold text-navy-950 transition-colors hover:bg-gold-500"
            >
              {primaryLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </button>
          ) : primaryHref ? (
            <Link
              to={primaryHref}
              data-testid="cta-get-started-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gold-600 px-7 py-4 text-base font-bold text-navy-950 transition-colors hover:bg-gold-500"
            >
              {primaryLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => scrollToId('#solution-finder')}
              data-testid="cta-get-started-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gold-600 px-7 py-4 text-base font-bold text-navy-950 transition-colors hover:bg-gold-500"
            >
              {primaryLabel}
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
            </button>
          )}
          {secondaryHref.startsWith('#') ? (
            <button
              type="button"
              onClick={() => scrollToId(secondaryHref)}
              data-testid="cta-explore-services-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md border-2 border-white/30 px-7 py-[14px] text-base font-bold text-white transition-colors hover:border-white hover:bg-white hover:text-navy-900"
            >
              {secondaryLabel}
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
            </button>
          ) : (
            <Link
              to={secondaryHref}
              data-testid="cta-explore-services-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-md border-2 border-white/30 px-7 py-[14px] text-base font-bold text-white transition-colors hover:border-white hover:bg-white hover:text-navy-900"
            >
              {secondaryLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      </Reveal>
    </div>
  </section>
  );
};
